"use client";

import * as React from "react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Meridiem = "AM" | "PM";

/** HH 또는 H:MM 형태를 HH:mm으로 정규화. 유효하지 않으면 null */
function normalizeTimeString(s: string): string | null {
  const t = s.trim();
  if (!t) return null;
  const match = t.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

function isAllowedTimeDraft(value: string) {
  if (value === "") return true;
  if (!/^\d{0,2}:?\d{0,2}$/.test(value)) return false;

  const [hourText, minuteText = ""] = value.split(":");
  if (hourText.length > 2 || minuteText.length > 2) return false;
  if (value.includes(":") && hourText.length === 0) return false;

  const hour = Number(hourText);
  if (hourText.length === 2 && (hour < 1 || hour > 12)) return false;

  if (minuteText.length === 2) {
    const minute = Number(minuteText);
    if (minute < 0 || minute > 59) return false;
  }

  return true;
}

function parseTwentyFourHour(value: string | null) {
  const normalized = value ? normalizeTimeString(value) : null;
  if (!normalized) return null;

  const [hourText, minuteText] = normalized.split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const period: Meridiem = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;

  return {
    period,
    time: `${hour12.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`,
  };
}

function toTwentyFourHour(period: Meridiem, value: string): string | null {
  const normalized = normalizeTimeString(value);
  if (!normalized) return null;

  const [hourText, minuteText] = normalized.split(":");
  const rawHour = Number(hourText);
  const minute = Number(minuteText);
  if (rawHour < 1 || rawHour > 12) return null;

  const hour =
    period === "AM"
      ? rawHour === 12
        ? 0
        : rawHour
      : rawHour === 12
        ? 12
        : rawHour + 12;

  return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
}

export interface TimePickerProps {
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  /** 테이블 셀용 스타일 (conf_shift_time 호환) */
  variant?: "default" | "table";
}

export function TimePicker({
  value,
  onChange,
  disabled = false,
  placeholder = "09:00",
  ariaLabel,
  className,
  variant = "default",
}: TimePickerProps) {
  const initial = parseTwentyFourHour(value);
  const [period, setPeriod] = React.useState<Meridiem>(
    initial?.period ?? "AM"
  );
  const [inputValue, setInputValue] = React.useState(initial?.time ?? "");

  React.useEffect(() => {
    const parsed = parseTwentyFourHour(value);
    setPeriod(parsed?.period ?? "AM");
    setInputValue(parsed?.time ?? "");
  }, [value]);

  const handleBlur = () => {
    const normalized = toTwentyFourHour(period, inputValue);
    if (normalized) {
      onChange(normalized);
      setInputValue(parseTwentyFourHour(normalized)?.time ?? "");
    } else if (value) {
      const parsed = parseTwentyFourHour(value);
      setPeriod(parsed?.period ?? "AM");
      setInputValue(parsed?.time ?? "");
    } else {
      setInputValue("");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    if (!isAllowedTimeDraft(nextValue)) return;

    setInputValue(nextValue);

    const normalized = toTwentyFourHour(period, nextValue);
    if (normalized) {
      onChange(normalized);
    }
  };

  const handlePeriodChange = (nextPeriod: Meridiem) => {
    setPeriod(nextPeriod);
    const normalized = toTwentyFourHour(nextPeriod, inputValue);
    if (normalized) {
      onChange(normalized);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;

    const normalized = toTwentyFourHour(period, inputValue);
    if (!normalized) {
      e.preventDefault();
      return;
    }

    onChange(normalized);
    setInputValue(parseTwentyFourHour(normalized)?.time ?? "");
  };

  if (variant === "table") {
    return (
      <div
        className={cn(
          "flex min-w-0 items-center justify-center gap-1",
          className
        )}
      >
        <Select
          value={period}
          disabled={disabled}
          onValueChange={(value) => handlePeriodChange(value as Meridiem)}
        >
          <SelectTrigger
            size="sm"
            aria-label={`${ariaLabel ?? "시간"} 오전 오후`}
            className="h-8 w-[58px] shrink-0 bg-white px-2 text-xs font-semibold text-sig"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="AM">오전</SelectItem>
              <SelectItem value="PM">오후</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
        <Input
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          aria-label={ariaLabel}
          className="h-8 w-16 min-w-0 border-[#d6d6de] bg-white px-1 font-mono text-xs text-center text-[#333] tabular-nums"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid w-full min-w-0 grid-cols-[84px_1fr] items-center gap-2",
        className
      )}
    >
      <Select
        value={period}
        disabled={disabled}
        onValueChange={(value) => handlePeriodChange(value as Meridiem)}
      >
        <SelectTrigger
          aria-label={`${ariaLabel ?? "시간"} 오전 오후`}
          className="h-9 w-full bg-white font-semibold text-sig"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value="AM">오전</SelectItem>
            <SelectItem value="PM">오후</SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      <Input
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        aria-label={ariaLabel}
        className="h-9 min-w-0 bg-white font-mono text-center tabular-nums"
      />
    </div>
  );
}
